import { useCallback, useEffect, useState, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, TextField } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';

import DataTypeSearchBox from './components/DatatypeSearchBox';
import { useStyles } from './components/styles';
import TaggedItems from './components/TaggedItems';

import { useAccountsCount } from '../../../../hooks/useAccountsEntityCount';
import { EditRateRecCategoryDrawerProps, rateRecCategoryOption } from '../../../../models';
import { selectAccountsEntityCount } from '../../../../selectors/accountsEntityCount';
import { selectCategories } from '../../../../selectors/categories';
import LoadingSpinner from '../../../LoadingSpinner';
import { DataSelectionType } from '../../sharedCategoryDrawerUtils';

export type SearchboxElementType = {
  accountNumber?: string;
  name: string;
  count: number;
  isDisabled?: boolean;
  prevSelected?: boolean;
  stepName?: string;
  dataCategory?: string | null;
};

const EditRateRecCategoryDrawer: React.FC<EditRateRecCategoryDrawerProps> = ({
  isOpen,
  onClose = () => null,
  selectedRateRecCategory,
  rateRecCategories,
  currentContainer,
  updateData
}: EditRateRecCategoryDrawerProps) => {
  const classes = useStyles();
  const adjustmentsByDataType = useSelector(selectAccountsEntityCount);
  const { getUseAccountsCount } = useAccountsCount();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [categoryName, setCategoryName] = useState<string>('');
  const [accounts, setAccounts] = useState<SearchboxElementType[]>([]);
  const [initialSelectedAccounts, setInitialSelectedAccounts] = useState<SearchboxElementType[]>([]);
  const [selectedAccountsEntityCount, setSelectedAccountsEntityCount] = useState<SearchboxElementType[]>([]);
  const [selectedDataType, setSelectedDataType] = useState<DataSelectionType>();
  const [dataTypes, setDataTypes] = useState<DataSelectionType[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const categoriesData = useSelector(selectCategories);
  // To handle memory leak warning
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const isSame =
      selectedAccountsEntityCount.length === initialSelectedAccounts.length &&
      selectedAccountsEntityCount.every((item) =>
        initialSelectedAccounts.some(
          (initial) =>
            item.name === initial.name &&
            item.count === initial.count &&
            item.stepName === initial.stepName &&
            item.dataCategory === initial.dataCategory &&
            item.accountNumber === initial.accountNumber
        )
      ) &&
      initialSelectedAccounts.every((initial) =>
        selectedAccountsEntityCount.some(
          (item) =>
            item.name === initial.name &&
            item.count === initial.count &&
            item.stepName === initial.stepName &&
            item.dataCategory === initial.dataCategory &&
            item.accountNumber === initial.accountNumber
        )
      );
    setHasChanges(!isSame);
  }, [selectedAccountsEntityCount, initialSelectedAccounts]);

  useEffect(() => {
    setDataTypes(rateRecCategoryOption(selectedRateRecCategory?.name));
  }, [selectedRateRecCategory?.name]);

  const accountToCategoryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of categoriesData.categories) {
      for (const account of category.accounts) {
        const { attributeName, stepName } = account;
        const compositeKey = `${attributeName}-${stepName}`;
        map.set(compositeKey, category.name);
      }
    }

    return map;
  }, [categoriesData?.categories]);

  // Populates Initial Tagged Items
  useEffect(() => {
    setCategoryName(selectedRateRecCategory?.name ?? '');
    if (selectedRateRecCategory?.adjustments) {
      const initialAccounts: SearchboxElementType[] = selectedRateRecCategory.adjustments.map(
        (adj: {
          attributeName: string;
          numberOfEntities: number;
          stepName?: string;
          dataCategory?: string | null;
          accountNumber?: string;
        }) => {
          return {
            name: adj.attributeName,
            count: adj.numberOfEntities,
            stepName: adj.stepName,
            dataCategory: null,
            accountNumber: adj.accountNumber
          };
        }
      );
      if (categoriesData?.categories?.length > 0 && initialAccounts.length > 0) {
        initialAccounts.forEach((element) => {
          const { name, stepName } = element;
          if (name && stepName) {
            const key = `${name}-${stepName}`;
            if (accountToCategoryMap.has(key)) {
              element.dataCategory = accountToCategoryMap.get(key) ?? null;
            }
          }
        });
        initialAccounts.sort((a: SearchboxElementType, b: SearchboxElementType) => {
          const stepA = a.stepName ?? '';
          const stepB = b.stepName ?? '';
          if (stepA !== stepB) {
            return stepA.localeCompare(stepB);
          }

          const catA = a.dataCategory ?? '';
          const catB = b.dataCategory ?? '';
          if (catA !== catB) {
            return catA.localeCompare(catB);
          }

          const nameA = a.name ?? '';
          const nameB = b.name ?? '';
          if (nameA !== nameB) {
            return nameA.localeCompare(nameB);
          }

          const accountNumberA = a.accountNumber ?? '';
          const accountNumberB = b.accountNumber ?? '';
          if (accountNumberA !== accountNumberB) {
            return accountNumberA.localeCompare(accountNumberB);
          }

          return 0;
        });
      }

      setSelectedAccountsEntityCount(initialAccounts);
      setInitialSelectedAccounts(initialAccounts);
    } else {
      setSelectedAccountsEntityCount([]);
    }
  }, [categoriesData, selectedRateRecCategory, accountToCategoryMap]);

  // Populates Data Type Search Adjustments
  useEffect(() => {
    const otherRateRecCategory = rateRecCategories.filter(
      (rateRecCategory: any) => rateRecCategory.rateRecCategoryId !== selectedRateRecCategory.id
    );
    const allOtherAdjustments = otherRateRecCategory.flatMap((cat: any) => cat.adjustments || []);

    const parsedSearchboxElements: SearchboxElementType[] = adjustmentsByDataType.accounts
      .filter((account) => {
        return !allOtherAdjustments.some(
          ({ attributeName, stepName }: { attributeName: string; stepName: string }) =>
            attributeName === account.accountName && stepName === account.stepName
        );
      })
      .map(({ accountName, entityCount, accountNumber, stepName }) => ({
        name: accountName,
        count: entityCount,
        stepName,
        dataCategory: null,
        accountNumber
      }));
    if (categoriesData?.categories?.length > 0 && parsedSearchboxElements.length > 0) {
      parsedSearchboxElements.forEach((element) => {
        const { name, stepName } = element;
        if (name && stepName) {
          const key = `${name}-${stepName}`;
          if (accountToCategoryMap.has(key)) {
            element.dataCategory = accountToCategoryMap.get(key) ?? null;
          }
        }
      });
      parsedSearchboxElements.sort((a: SearchboxElementType, b: SearchboxElementType) => {
        const catA = a.dataCategory ?? '';
        const catB = b.dataCategory ?? '';
        if (catA !== catB) {
          return catA.localeCompare(catB);
        }

        const nameA = a.name ?? '';
        const nameB = b.name ?? '';
        if (nameA !== nameB) {
          return nameA.localeCompare(nameB);
        }

        const accountNumberA = a.accountNumber ?? '';
        const accountNumberB = b.accountNumber ?? '';
        if (accountNumberA !== accountNumberB) {
          return accountNumberA.localeCompare(accountNumberB);
        }

        return 0;
      });
    }

    setAccounts(parsedSearchboxElements);
  }, [adjustmentsByDataType, categoriesData, selectedRateRecCategory, accountToCategoryMap, rateRecCategories]);

  const onSelectAccountsEntityCount = (selected: SearchboxElementType[]) => {
    setSelectedAccountsEntityCount(selected);
  };

  const onChangeDataType = useCallback(
    async (_: any, selected: DataSelectionType | undefined) => {
      if (selected) {
        await getUseAccountsCount(`${selected.level}.${selected.step}`);
        setSelectedDataType(selected);
      }
    },
    [getUseAccountsCount]
  );

  const deleteAccountFromCategory = (
    accountName: string,
    stepName?: string | undefined,
    dataCategory?: string | null | undefined
  ) => {
    setSelectedAccountsEntityCount((prev) =>
      prev.filter((account) =>
        dataCategory !== null && dataCategory === account.dataCategory
          ? false
          : !(account.name === accountName && account.stepName === stepName)
      )
    );
  };

  const onSave = useCallback(async () => {
    // eslint-disable-next-line unicorn/no-useless-undefined
    if (isMountedRef.current) setSelectedDataType(undefined);
    if (isMountedRef.current) setIsSaving(true);
    const adjustmentsToAdd = selectedAccountsEntityCount.filter(
      (account) =>
        !initialSelectedAccounts.some(
          (initial) => initial.name === account.name && initial.stepName === account.stepName
        )
    );
    const adjustmentsToDelete = initialSelectedAccounts.filter(
      (account) =>
        !selectedAccountsEntityCount.some(
          (selected) => selected.name === account.name && selected.stepName === account.stepName
        )
    );
    const adjustmentsPayload = [...adjustmentsToAdd, ...adjustmentsToDelete].map((adjustment) => ({
      ...adjustment,
      attributeName: adjustment.name
    }));
    const formatted = adjustmentsPayload.map((adjustment) => ({ ...adjustment, attributeName: adjustment.name }));
    await updateData(selectedRateRecCategory.id, formatted);
    if (isMountedRef.current) setIsSaving(false);
    onClose();
  }, [selectedRateRecCategory, selectedAccountsEntityCount, onClose, updateData, initialSelectedAccounts]);

  const onCloseReset = () => {
    // eslint-disable-next-line unicorn/no-useless-undefined
    setSelectedDataType(undefined);

    if (selectedRateRecCategory?.adjustments) {
      setSelectedAccountsEntityCount(initialSelectedAccounts);
    } else {
      setSelectedAccountsEntityCount([]);
    }

    setHasChanges(false);
    onClose();
  };

  return isSaving ? (
    <Drawer open={isOpen} className={classes.rateRecDrawer} anchor="right">
      <Box className={classes.loadingContainer}>
        <LoadingSpinner />
      </Box>
    </Drawer>
  ) : (
    <Drawer open={isOpen} className={classes.rateRecDrawer} anchor="right">
      <Box className={classes.editingCategoryBar}>
        <Typography className={classes.editingCategoryBarText}>{t('editing-category')}</Typography>
      </Box>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <Typography variant="h3">{categoryName}</Typography>
        </Box>
        <Box className={classes.closeButton}>
          <IconButton role="button" onClick={onCloseReset}>
            <img src="/imgs/panel_close.svg" />
          </IconButton>
        </Box>
      </Box>
      <Box
        className={classes.container}
        data-roles-disable-element={currentContainer?.isFinalized ? 'finalizedContainer' : ''}
      >
        <Box className={classes.row}>
          <Box className={classes.categoryName}>
            <Typography variant="h3" className={classes.fileNameText}>
              {t('Category Name')}
            </Typography>
            <TextField
              fullWidth
              disabled
              placeholder={t('Add Name')}
              autoComplete="off"
              variant="outlined"
              size="small"
              value={selectedRateRecCategory.name}
            />
          </Box>
        </Box>
        <DataTypeSearchBox
          dataTypes={dataTypes}
          selectedDataType={selectedDataType}
          accounts={accounts}
          adjustmentsByDataType={adjustmentsByDataType}
          selectedAccountsEntityCount={selectedAccountsEntityCount}
          onSelectAccountsEntityCount={onSelectAccountsEntityCount}
          onChangeDataType={onChangeDataType}
        />
        <TaggedItems
          selectedAccountsEntityCount={selectedAccountsEntityCount}
          deleteAccountFromCategory={deleteAccountFromCategory}
        />
      </Box>
      <Box className={classes.footer}>
        <Box>
          <Button variant="text" onClick={onCloseReset}>
            <b>{t('Cancel')}</b>
          </Button>
          <Button variant="outlined" disabled={!hasChanges} onClick={onSave}>
            {t('Save')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditRateRecCategoryDrawer;
